/** API service for scorecard storage and retrieval. */

import axios, { AxiosResponse } from 'axios';
import { auth } from '../lib/firebase';
import type { MandateOverview } from '@/types/mandateOverview';

// API base URL - use environment variable or relative path for vite proxy
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance for scorecard API
const scorecardApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor to include Firebase token
scorecardApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
  }
  return config;
});

export interface ScorecardMetadata {
  id: string;
  user_id: string;
  script_title?: string | null;
  created_at?: any; // Firestore timestamp
  audience_type?: string | null; // "creator" | "decision_maker" for display labels
  analysis_metadata?: {
    etb_score?: number;
    conceptual_score?: number;
    emotional_score?: number;
    overall_score?: number;
    engagement_percentage?: number;
    mandate_match_pct?: number;
    passes_mandate?: boolean;
    recommendation_label?: string;
  };
}

export interface ScorecardDetail extends ScorecardMetadata {
  scorecard: any; // Full scorecard data
  blueprint?: any;
  /** V7 canonical blueprint (may also live on blueprint.blueprint_markdown). */
  blueprint_markdown?: string | null;
  /** Raw agent outputs for Detailed Analysis (when retrieving previous analysis) */
  etb_analysis?: string | null;
  conceptual_analysis?: string | null;
  emotional_analysis?: string | null;
  genre_analysis?: string | null;
  scene_analysis?: string | null;
  dialogue_analysis?: string | null;
  mandate_overview?: MandateOverview | null;
}

export interface ScorecardsListResponse {
  status: 'success' | 'error';
  scorecards: ScorecardMetadata[];
  limit: number;
  offset: number;
}

export interface ScorecardDetailResponse {
  status: 'success' | 'error';
  scorecard: ScorecardDetail;
}

/**
 * List user's scorecards.
 * @param limit - Maximum number of scorecards to return (default: 20)
 * @param offset - Number of scorecards to skip (default: 0)
 * @param sortBy - Field to sort by (default: "created_at")
 * @returns List of scorecard metadata
 */
export async function getScorecards(
  limit: number = 20,
  offset: number = 0,
  sortBy: string = 'created_at'
): Promise<ScorecardMetadata[]> {
  try {
    const response: AxiosResponse<ScorecardsListResponse> = await scorecardApi.get(
      '/api/scorecards',
      {
        params: { limit, offset, sort_by: sortBy },
      }
    );

    if (response.data.status === 'error') {
      throw new Error('Failed to get scorecards');
    }

    return response.data.scorecards;
  } catch (error: any) {
    console.error('Error getting scorecards:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to get scorecards');
  }
}

/**
 * Get full scorecard details by ID.
 * @param scorecardId - The scorecard ID (session_id)
 * @returns Full scorecard data
 */
export async function getScorecardById(scorecardId: string): Promise<ScorecardDetail> {
  try {
    const response: AxiosResponse<ScorecardDetailResponse> = await scorecardApi.get(
      `/api/scorecards/${scorecardId}`
    );

    if (response.data.status === 'error') {
      throw new Error('Failed to get scorecard');
    }

    return response.data.scorecard;
  } catch (error: any) {
    console.error('Error getting scorecard:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to get scorecard');
  }
}

/**
 * Delete a scorecard and its associated script data (Firestore + GCS).
 * @param scorecardId - The scorecard ID (session_id)
 */
export async function deleteScorecard(scorecardId: string): Promise<void> {
  try {
    const response = await scorecardApi.delete(`/api/scorecards/${scorecardId}`);
    if (response.data?.status === 'error') {
      throw new Error(response.data.message || 'Failed to delete scorecard');
    }
  } catch (error: any) {
    console.error('Error deleting scorecard:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to delete scorecard');
  }
}
